import ExcelJS from "exceljs";
import { groupByProductId } from "./exportImageUtils";
import { embedPhoto } from "./excelPhoto";

const COMPANY = [
  "Better Home Fashion Inc",
  "280 Bowes Rd, Concord, ON L4K 1J9",
  "Tel: 905-660-8741   Fax: 416-352-5796",
];

const PHOTO_COL_WIDTH = 40;
const PHOTO_ROW_HEIGHT_PT = 73.3;

// iter 186 (2026-02): the export is now column-driven — the columns rendered,
// their order, widths and header labels all derive from FIELD_META filtered
// by the user's saved selection in ExportFieldsDialog. Product-level columns
// (photo, description, material) still get their cells merged across every
// variant row in a group; variant-level columns render per-row.
const FIELD_META = {
  photo:       { label: "Photo",     width: PHOTO_COL_WIDTH, level: "product", isPhoto: true },
  description: { label: "Description", width: 45, level: "product", valueFn: (r) => r.description || "-", wrap: true },
  material:    { label: "Material",    width: 30, level: "product", valueFn: (r) => r.specification || "-", wrap: true },
  item_no:     { label: "Item No",     width: 18, level: "variant", valueFn: (r) => r.item_no || "-", bold: true },
  size:        { label: "Size",        width: 22, level: "variant", valueFn: (r) => r.size || "-" },
  pcs_per_ctn: { label: "Pcs/CTN",     width: 10, level: "variant", valueFn: (r) => r.pcs_per_ctn ?? "-", align: "right" },
  // iter 248: carton dims are stored in cm (same as UI) — no unit conversion. CBM is already stored per-carton (m³); qty_40ft
  // reflects the effective qty after compression_rate + entry override.
  carton_dims: { label: "Carton L\u00d7W\u00d7H (cm)", width: 20, level: "variant",
                 valueFn: (r) => (r.carton_length && r.carton_width && r.carton_height
                                   ? `${r.carton_length}\u00d7${r.carton_width}\u00d7${r.carton_height}`
                                   : "-") },
  cbm:         { label: "CBM/CTN",     width: 12, level: "variant",
                 valueFn: (r) => (r.cbm != null ? Number(r.cbm).toFixed(4) : "-"), align: "right" },
  qty_40ft:    { label: "Qty/40'HQ",   width: 12, level: "variant",
                 valueFn: (r) => (r.qty_40ft != null ? Number(r.qty_40ft).toLocaleString() : "-"), align: "right" },
  // iter 278: QTY = available to this customer (CI − other quotations' reservations); Reserved = held for this quotation.
  qty:         { label: "QTY",         width: 10, level: "variant", valueFn: (r) => r.ci_available ?? r.ci_inventory_qty ?? "-", align: "right" },
  reserved_qty: { label: "Reserved QTY", width: 12, level: "variant", valueFn: (r) => (r.ci_reserved_qty ? r.ci_reserved_qty : "-"), align: "right" },
  // iter 221 (2026-02): 4 个可选新字段。都是 variant 级,不参与 rowspan 合并。
  gt_sku:         { label: "GT SKU",           width: 14, level: "variant", valueFn: (r) => r.gt_sku || "-" },
  order_qty:      { label: "Order QTY",        width: 12, level: "variant",
                    valueFn: (r) => (r.qty != null ? Number(r.qty).toLocaleString() : "-"), align: "right" },
  purchase_price: { label: "Purchase (RMB)",   width: 14, level: "variant",
                    valueFn: (r) => (r.purchase_price != null ? Number(r.purchase_price).toFixed(2) : "-"), align: "right" },
  gt_price:       { label: "GT Price",         width: 12, level: "variant",
                    valueFn: (r) => (r.gt_price != null ? Number(r.gt_price).toFixed(2) : "-"), align: "right" },
  barcode:     { label: "Barcode",     width: 16, level: "variant", valueFn: (r) => r.upc || "-" },
  price:       { label: "Price",       width: 12, level: "variant", isPrice: true, align: "right", bold: true },
};

export async function exportQuotationExcel(rows, filename = "quotation", currency = "USD", fields) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Quotation");
  const priceNumFmt = { USD: '"$"#,##0.00', CAD: '"C$"#,##0.00', CNY: '"¥"#,##0.00' }[currency] || '"$"#,##0.00';

  // Build the active-column list. Keep FIELD_META declaration order so the
  // exported layout is predictable regardless of which order fields were
  // toggled in the dialog. `fields` unset → export every column.
  const orderedIds = Object.keys(FIELD_META);
  const activeIds = fields
    ? orderedIds.filter((id) => fields.includes(id))
    : orderedIds;
  const cols = activeIds.map((id) => ({ id, ...FIELD_META[id] }));
  const totalCols = cols.length || 1;
  sheet.columns = cols.map((c) => ({ width: c.width }));
  const colIndexOf = (id) => activeIds.indexOf(id) + 1; // 1-based, 0 if not present

  COMPANY.forEach((line, idx) => {
    const rowNum = idx + 1;
    sheet.mergeCells(rowNum, 1, rowNum, totalCols);
    const cell = sheet.getCell(rowNum, 1);
    cell.value = line;
    cell.font = { bold: idx === 0, size: idx === 0 ? 14 : 10 };
  });

  const headerRowNum = 5;
  const headerRow = sheet.getRow(headerRowNum);
  cols.forEach((c, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = c.label;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = {
      top: { style: "thin", color: { argb: "FFD1D5DB" } },
      bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
      left: { style: "thin", color: { argb: "FFD1D5DB" } },
      right: { style: "thin", color: { argb: "FFD1D5DB" } },
    };
  });

  const groupSize = groupByProductId(rows);
  let currentRow = headerRowNum + 1;

  for (let idx = 0; idx < rows.length; idx++) {
    const r = rows[idx];
    const rowNum = currentRow;
    const isGroupStart = groupSize[idx] > 0;
    const isSingleVariant = isGroupStart && groupSize[idx] === 1;
    const rowHeightPt = isSingleVariant ? PHOTO_ROW_HEIGHT_PT * 2 : PHOTO_ROW_HEIGHT_PT;

    sheet.getRow(rowNum).height = rowHeightPt;

    // Variant-level cells render on every row.
    cols.forEach((c, i) => {
      if (c.level !== "variant") return;
      const col = i + 1;
      let val;
      if (c.isPrice) {
        val = r.final_price != null ? r.final_price : "-";
        if (r.final_price != null) sheet.getCell(rowNum, col).numFmt = priceNumFmt;
      } else {
        val = c.valueFn(r);
      }
      sheet.getCell(rowNum, col).value = val;
      if (c.bold) sheet.getCell(rowNum, col).font = { bold: true };
      if (c.align) sheet.getCell(rowNum, col).alignment = { horizontal: c.align, vertical: "middle" };
    });

    if (isGroupStart) {
      const span = groupSize[idx];
      // Merge product-level cells across the variants and write their values.
      cols.forEach((c, i) => {
        if (c.level !== "product") return;
        const col = i + 1;
        if (span > 1) sheet.mergeCells(rowNum, col, rowNum + span - 1, col);
        if (c.isPhoto) return; // photo handled below
        sheet.getCell(rowNum, col).value = c.valueFn(r);
        if (c.wrap) sheet.getCell(rowNum, col).alignment = { wrapText: true, vertical: "middle" };
      });

      const photoCol = colIndexOf("photo");
      if (photoCol && r.photo_url) {
        // eslint-disable-next-line no-await-in-loop
        await embedPhoto({ workbook, sheet, photoUrl: r.photo_url, rowNum, col: photoCol, rowHeightPt, span, colWidthChars: PHOTO_COL_WIDTH });
      }
    }

    for (let col = 1; col <= totalCols; col++) {
      sheet.getCell(rowNum, col).border = {
        top: { style: "thin", color: { argb: "FFD1D5DB" } },
        bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
        left: { style: "thin", color: { argb: "FFD1D5DB" } },
        right: { style: "thin", color: { argb: "FFD1D5DB" } },
      };
    }

    currentRow += 1;
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
